import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { Html5Qrcode } from 'html5-qrcode';
import { motion } from 'framer-motion';
import {
  Swords, QrCode, ScanLine, Trophy, Clock, Battery, Target,
  Flame, Shield, PawPrint,
} from 'lucide-react';
import { api, compact, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { themeOf, ARENA } from '../lib/classTheme';
import {
  ArenaHeader, ArenaTitle, ArenaTabs, ArenaFloor, GoldFrame,
  Pedestal, Laurel, VersusEmblem, ArenaStatBar, ArenaEmpty,
} from '../components/ArenaFrame';
import { Avatar, Sheet, SkeletonList } from '../components/ui';

export default function Arena() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [qrOpen, setQrOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [stake, setStake] = useState(0);
  const [invite, setInvite] = useState(null);
  const [tab, setTab] = useState('duel');

  const { data: h } = useQuery({ queryKey: ['hero'], queryFn: () => api.get('/hero').then(r => r.data) });
  const { data: pet } = useQuery({ queryKey: ['pet'], queryFn: () => api.get('/pets').then(r => r.data) });
  const { data: active } = useQuery({
    queryKey: ['activeFights'], queryFn: () => api.get('/fights/active/me').then(r => r.data),
    refetchInterval: 15000,
  });
  const { data: opponents } = useQuery({
    queryKey: ['matchmake'], queryFn: () => api.get('/fights/matchmake').then(r => r.data),
  });
  const { data: history } = useQuery({
    queryKey: ['fightHistory'], queryFn: () => api.get('/fights/history/me').then(r => r.data),
  });

  const inviteM = useMutation({
    mutationFn: () => api.post('/fights/invite', { stake }),
    onSuccess: ({ data }) => { setInvite(data); setQrOpen(true); },
    onError: e => toast.error(e.message),
  });

  const challengeM = useMutation({
    mutationFn: (username) => api.post(`/fights/challenge/${username}`, { stake }),
    onSuccess: () => { toast.success('Challenge sent! ⚔️'); qc.invalidateQueries(['activeFights']); },
    onError: e => toast.error(e.message),
  });

  useEffect(() => {
    const s = getSocket(); if (!s) return;
    const onStart = ({ fightId }) => { toast.success('The crowd roars! 🔥'); nav(`/fight/${fightId}`); };
    s.on('fight:started', onStart);
    return () => s.off('fight:started', onStart);
  }, [nav]);

  const t = themeOf(h?.class_key);
  const energy = h?.energy;

  if (!h) return <SkeletonList n={4} h="h-40" />;

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══════ COLOSSEUM HEADER ═══════ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={h.class_key} className="p-5" showCrowd>
          <div className="flex items-center gap-4">
            <Pedestal glow={t.hex}>
              <div className="w-24 h-32 rounded-xl overflow-hidden"
                style={{ padding: 2, background: ARENA.goldFrame, boxShadow: t.glowCss }}>
                <div className="w-full h-full rounded-[10px] overflow-hidden bg-stone-900">
                  {h.image_url
                    ? <img src={h.image_url} alt="" className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full grid place-items-center text-2xl">{t.icon}</div>}
                </div>
              </div>
            </Pedestal>

            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-2 mb-1">
                <Laurel size={24} color={ARENA.goldL} />
                <span className="text-[10px] font-display font-black uppercase tracking-[0.2em]"
                  style={{ color: ARENA.goldL }}>
                  {t.romanTitle}
                </span>
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-wide"
                style={{ color: ARENA.goldL, textShadow: '0 2px 16px rgba(201,151,58,.5)' }}>
                Arena
              </h1>
              <p className="text-xs font-semibold" style={{ color: 'rgba(232,220,200,.7)' }}>
                {h.name} · {t.latin}
              </p>

              <div className="grid grid-cols-4 gap-1.5 mt-3">
                <Stat l="ELO"    v={h.elo} />
                <Stat l="WINS"   v={h.wins} />
                <Stat l="LOSSES" v={h.losses} />
                <Stat l="STREAK" v={`🔥${h.streak}`} />
              </div>
            </div>
          </div>

          {/* pet companion */}
          {pet?.pet && (
            <div className="mt-3 flex items-center gap-2.5 p-2 rounded-xl"
              style={{ background: 'rgba(232,200,116,.10)', border: '1px solid rgba(232,200,116,.25)' }}>
              <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0"
                style={{ border: `1.5px solid ${t.hex}` }}>
                <img src={pet.pet.thumb || pet.pet.image} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-display font-bold text-white truncate">
                  {pet.pet.name}
                </p>
                <p className="text-[9px] font-semibold" style={{ color: 'rgba(232,220,200,.6)' }}>
                  Lv{pet.pet.level} {pet.pet.species} · fights beside you
                </p>
              </div>
              <PawPrint size={15} style={{ color: ARENA.goldL }} />
            </div>
          )}

          {/* energy */}
          {energy && (
            <div className="mt-3">
              <div className="flex justify-between text-[10px] font-display font-bold mb-1"
                style={{ color: ARENA.goldL }}>
                <span className="flex items-center gap-1"><Battery size={11} /> STAMINA</span>
                <span>{energy.energy}/{energy.max}{energy.nextIn > 0 && ` · +1 in ${energy.nextIn}m`}</span>
              </div>
              <ArenaStatBar value={energy.energy} max={energy.max}
                color="linear-gradient(90deg,#34d399,#059669)" height={7} />
            </div>
          )}
        </ArenaFloor>

        {/* actions */}
        <div className="p-3 space-y-2" style={{ background: 'var(--sand-100)' }}>
          <div className="flex items-center gap-2">
            <label className="label !mb-0 shrink-0">Wager</label>
            <div className="flex gap-1.5 flex-1 overflow-x-auto no-scrollbar">
              {[0, 50, 100, 250, 500].map(v => (
                <button key={v} onClick={() => setStake(v)}
                  className="px-3 py-1.5 rounded-lg text-xs font-black whitespace-nowrap transition"
                  style={stake === v
                    ? { background: ARENA.goldFrame, color: '#fff', boxShadow: '0 2px 10px -2px rgba(201,151,58,.5)' }
                    : { background: 'var(--stone-100)', color: 'var(--stone-500)' }}>
                  {v === 0 ? 'Honor' : `💎${v}`}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => inviteM.mutate()} disabled={inviteM.isPending}
              className="btn-primary"><QrCode size={16} /> Show sigil</button>
            <button onClick={() => setScanOpen(true)} className="btn-dark">
              <ScanLine size={16} /> Scan sigil
            </button>
          </div>

          {stake > 0 && (
            <p className="text-[11px] text-center font-semibold" style={{ color: 'var(--stone-500)' }}>
              Victor claims {stake * 2} points from the pot
            </p>
          )}
        </div>
      </div>

      {/* ═══════ TABS ═══════ */}
      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'duel', label: 'Duels', icon: '⚔️' },
        { key: 'history', label: 'Records', icon: '📜' },
      ]} />

      {tab === 'duel' && (
        <>
          {/* ongoing */}
          {active?.fights?.length > 0 && (
            <div>
              <ArenaTitle title="Ongoing duels" latin="Certamina" laurel />
              <div className="space-y-2">
                {active.fights.map(f => {
                  const myTurn = (f.turn === 1 ? f.p1_id : f.p2_id) === user.id;
                  return (
                    <Link key={f.id} to={`/fight/${f.id}`}
                      className="card-h p-3 flex items-center gap-3"
                      style={myTurn ? {
                        borderColor: ARENA.gold,
                        boxShadow: '0 0 0 1px rgba(201,151,58,.3), 0 4px 20px -6px rgba(201,151,58,.4)',
                      } : {}}>
                      <div className={`w-10 h-10 rounded-xl grid place-items-center text-white
                        ${myTurn ? 'pulse-ring' : ''}`}
                        style={{ background: myTurn ? ARENA.goldFrame : 'var(--stone-200)',
                                 color: myTurn ? '#fff' : 'var(--stone-500)' }}>
                        <Swords size={18} />
                      </div>
                      <div className="flex-1">
                        <p className="font-display font-bold text-sm">{f.p1} vs {f.p2}</p>
                        <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                          Round {f.round}
                        </p>
                      </div>
                      <span className={myTurn ? 'chip-gold' : 'chip-ink'}>
                        {myTurn ? 'YOUR MOVE' : 'Waiting'}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* invites */}
          {active?.invites?.length > 0 && (
            <div>
              <ArenaTitle title="Challenges" latin="Provocationes" />
              <div className="space-y-2">
                {active.invites.map(i => (
                  <Link key={i.id} to={`/fight/accept/${i.code}`} className="card-h p-3 flex items-center gap-3">
                    <span className="text-2xl">⚔️</span>
                    <div className="flex-1">
                      <p className="font-display font-bold text-sm">@{i.username} challenges you</p>
                      <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                        {i.stake ? `Wager 💎${i.stake}` : 'Duel of honor'} · {timeAgo(i.created_at)}
                      </p>
                    </div>
                    <span className="btn-primary btn-sm">Accept</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* opponents */}
          <div>
            <ArenaTitle title="Worthy opponents" latin="Adversarii" sub="Similar rank to yours" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {!opponents ? <SkeletonList n={4} h="h-32" /> : opponents.map(o => {
                const ot = themeOf(o.class_key);
                return (
                  <div key={o.username} className="card p-2.5 text-center">
                    <Pedestal glow={ot.hex} width="70%">
                      <div className="w-16 h-20 mx-auto rounded-lg overflow-hidden"
                        style={{ padding: 1.5, background: ARENA.goldFrame }}>
                        <div className="w-full h-full rounded-md overflow-hidden bg-stone-200">
                          <img src={o.image_url} alt="" className="w-full h-full object-cover object-top" />
                        </div>
                      </div>
                    </Pedestal>
                    <p className="font-display font-bold text-[11px] truncate mt-2">{o.name}</p>
                    <p className="text-[9px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                      Lv{o.level} · {o.elo} ELO
                    </p>
                    <button onClick={() => challengeM.mutate(o.username)}
                      disabled={challengeM.isPending}
                      className="btn-primary btn-sm w-full mt-2">
                      <Target size={11} /> Challenge
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ═══════ HISTORY ═══════ */}
      {tab === 'history' && (
        !history?.length
          ? <ArenaEmpty emoji="📜" title="No records yet"
              sub="Scan a friend's sigil to begin your first duel." />
          : <div className="card p-2 space-y-1">
              {history.slice(0, 20).map(f => {
                const isP1 = f.p1_id === user.id;
                const opp = isP1 ? f.p2_name : f.p1_name;
                const oppImg = isP1 ? f.p2_img : f.p1_img;
                const won = f.winner_id === user.id;
                const draw = !f.winner_id && f.state === 'finished';
                return (
                  <Link key={f.id} to={`/fight/${f.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl transition hover:bg-stone-100">
                    <Avatar src={oppImg} name={opp} size={36} square gold />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm truncate">vs @{opp}</p>
                      <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                        {timeAgo(f.created_at)}
                      </p>
                    </div>
                    <span className={draw ? 'chip-ink' : won ? 'chip-gold' : 'chip-blood'}>
                      {f.state === 'active' ? 'LIVE'
                        : draw ? 'DRAW'
                        : won ? `WIN +${isP1 ? f.p1_elo_delta : f.p2_elo_delta}`
                        : `LOSS ${isP1 ? f.p1_elo_delta : f.p2_elo_delta}`}
                    </span>
                  </Link>
                );
              })}
            </div>
      )}

      {/* ═══════ QR SHEET ═══════ */}
      <Sheet open={qrOpen} onClose={() => setQrOpen(false)} title="Your battle sigil">
        <div className="text-center">
          <GoldFrame className="inline-block p-4" thick>
            {invite && <QRCodeSVG value={invite.qrPayload} size={190} level="M" fgColor="#241e16" />}
          </GoldFrame>
          <p className="mt-4 font-display font-black text-2xl tracking-[0.25em] grad-text">
            {invite?.code}
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--stone-500)' }}>
            Let your opponent scan this sigil.<br />
            {invite?.stake > 0 && <b>Wager: 💎{invite.stake}</b>}
          </p>
          <div className="chip-ink mt-3"><Clock size={11} /> Expires in 15 minutes</div>
        </div>
      </Sheet>

      {/* ═══════ SCANNER ═══════ */}
      <Sheet open={scanOpen} onClose={() => setScanOpen(false)} title="Scan opponent sigil">
        <Scanner onCode={(code) => { setScanOpen(false); nav(`/fight/accept/${code}`); }} />
      </Sheet>
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center backdrop-blur"
      style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.22)' }}>
      <p className="text-[8px] font-display font-bold tracking-wider"
        style={{ color: 'rgba(232,220,200,.6)' }}>{l}</p>
      <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>{v}</p>
    </div>
  );
}

function Scanner({ onCode }) {
  const [manual, setManual] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    let scanner;
    (async () => {
      try {
        scanner = new Html5Qrcode('qr-reader');
        ref.current = scanner;
        await scanner.start({ facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (text) => {
            const code = text.startsWith('wof:fight:') ? text.slice(10) : text.trim();
            scanner.stop().catch(() => {});
            onCode(code);
          }, () => {});
      } catch { toast.error('Camera unavailable — enter the code manually'); }
    })();
    return () => { try { ref.current?.stop(); } catch {} };
  }, [onCode]);

  return (
    <div>
      <GoldFrame dark>
        <div id="qr-reader" className="aspect-square" />
      </GoldFrame>
      <div className="divider-laurel"><span>⚔️</span></div>
      <label className="label">Or enter the sigil code</label>
      <div className="flex gap-2">
        <input className="input font-display font-black tracking-widest text-center uppercase"
          value={manual} maxLength={16} placeholder="ABC123XYZ"
          onChange={e => setManual(e.target.value.toUpperCase())} />
        <button className="btn-primary" disabled={manual.length < 6}
          onClick={() => onCode(manual)}>Enter</button>
      </div>
    </div>
  );
}